import type { ReactNode } from "react"

import { cn } from "@/lib/cn"

/**
 * Insignia: una etiqueta corta de estado.
 *
 * ⚠ **El texto siempre se escribe.** El color no puede ser el único portador de
 * significado (WCAG 1.4.1): «Activo» en verde e «Inactivo» en gris son la misma
 * insignia para quien no distingue esos dos colores, y en una impresión en blanco y
 * negro lo son para todo el mundo. Por eso el contenido es obligatorio y el tono es
 * el refuerzo, nunca el mensaje.
 *
 * ⚠ **Seis tonos, no una paleta libre.** Se corresponden con los tokens semánticos
 * del sistema, y ésa es toda la libertad que hay: con un `color` suelto por sitio,
 * el verde de «pagado» acaba siendo distinto del de «activo» y nadie sabe si la
 * diferencia significa algo.
 *
 * Para el estatus operativo del expediente existe `BadgeEstatus`, que agrupa los
 * once estados en cinco semánticas y añade un icono por grupo. Ésta es la genérica.
 */

export type Tono = "neutro" | "primario" | "exito" | "aviso" | "peligro" | "info"

/** El fondo es el mismo color al 12 %: tinta el chip sin competir con el texto. */
const COLOR: Record<Tono, string> = {
  neutro: "var(--fg-muted)",
  // La TINTA del acento, no su relleno: el chip pinta el texto de este color
  // sobre un lavado suyo al 12 %, y el relleno borgoña sobre ese lavado no
  // llegaria a 4,5:1 en modo oscuro.
  primario: "var(--primary-texto)",
  exito: "var(--success)",
  aviso: "var(--warning)",
  peligro: "var(--destructive)",
  info: "var(--info)",
}

export function Insignia({
  children,
  tono = "neutro",
  icono,
  className,
  ...resto
}: {
  children: ReactNode
  tono?: Tono
  icono?: ReactNode
  className?: string
} & React.HTMLAttributes<HTMLSpanElement>) {
  const color = COLOR[tono]
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        className,
      )}
      style={{
        color,
        // `color-mix` deja el fondo derivado del mismo token: al cambiar de tema o
        // de paleta, el relleno sigue al texto en vez de quedarse en un valor viejo.
        backgroundColor: `color-mix(in oklab, ${color} 12%, transparent)`,
        borderColor: `color-mix(in oklab, ${color} 28%, transparent)`,
      }}
      {...resto}
    >
      {icono}
      {children}
    </span>
  )
}
